<script setup lang="ts">
import { Application, Container, Graphics, Sprite, Text, Texture } from 'pixi.js'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { worldToPixel } from '../../core/map/coordinates'
import type {
  CoordinateCalibration,
  MapAsset,
  OutpostState,
  PlayerState,
  QuestState,
  RoutePlan
} from '../../types/game'

const props = defineProps<{
  map: MapAsset | null
  calibration: CoordinateCalibration | null
  player: PlayerState
  outposts: OutpostState[]
  quests: QuestState[]
  route: RoutePlan | null
}>()

const host = ref<HTMLDivElement | null>(null)
let app: Application | null = null
let world: Container | null = null
let terrainLayer: Container | null = null
let routeLayer: Container | null = null
let markerLayer: Container | null = null
let dragging = false
let previousPointer = { x: 0, y: 0 }
let renderToken = 0

function clearLayer(layer: Container | null) {
  if (!layer) return
  for (const child of layer.removeChildren()) child.destroy()
}

function fallbackSize() {
  return {
    width: Math.max(props.map?.width ?? 1200, 1),
    height: Math.max(props.map?.height ?? 800, 1)
  }
}

function drawMetadataFallback() {
  if (!terrainLayer) return
  const { width, height } = fallbackSize()
  const background = new Graphics()
    .rect(0, 0, width, height)
    .fill({ color: 0x111923 })
    .stroke({ color: 0x344256, width: 2 })
  const grid = new Graphics()
  for (let x = 0; x <= width; x += 128) grid.moveTo(x, 0).lineTo(x, height)
  for (let y = 0; y <= height; y += 128) grid.moveTo(0, y).lineTo(width, y)
  grid.stroke({ color: 0x2b394d, width: 1, alpha: 0.55 })
  const label = new Text({
    text: props.map
      ? '该 v4 文件只带地图元数据\n需要含 PNG Base64 的单文件才能显示真实像素'
      : '没有可用地图记录',
    style: {
      fill: 0xaab7c9,
      fontFamily: 'Microsoft YaHei, sans-serif',
      fontSize: 20,
      align: 'center'
    }
  })
  label.anchor.set(0.5)
  label.position.set(width / 2, height / 2)
  terrainLayer.addChild(background, grid, label)
}

async function drawTerrain() {
  const token = ++renderToken
  clearLayer(terrainLayer)
  if (!terrainLayer || !props.map?.objectUrl) {
    drawMetadataFallback()
    resetView()
    return
  }

  try {
    const image = new Image()
    image.decoding = 'async'
    const loaded = new Promise<void>((resolve, reject) => {
      image.onload = () => resolve()
      image.onerror = () => reject(new Error('PNG 加载失败'))
    })
    image.src = props.map.objectUrl
    await loaded
    if (token !== renderToken || !terrainLayer) return
    terrainLayer.addChild(new Sprite(Texture.from(image)))
  } catch {
    if (token !== renderToken) return
    drawMetadataFallback()
  }
  resetView()
}

function outpostPixel(outpost: OutpostState) {
  if (
    !props.calibration
    || outpost.estimatedWorldX === null
    || outpost.estimatedWorldY === null
  ) return null
  return worldToPixel(
    { x: outpost.estimatedWorldX, y: outpost.estimatedWorldY },
    props.calibration
  )
}

function drawMarkers() {
  clearLayer(markerLayer)
  if (!markerLayer || !props.calibration) return

  const playerPixel = worldToPixel(props.player, props.calibration)
  if (playerPixel) {
    const player = new Graphics()
      .circle(playerPixel.x, playerPixel.y, 11)
      .fill({ color: 0x77e58a })
      .stroke({ color: 0xf1fff4, width: 3 })
    markerLayer.addChild(player)
  }

  const questTargetIds = new Set(
    props.quests.flatMap((quest) => (
      quest.targetOutpostId === null ? [] : [quest.targetOutpostId]
    ))
  )
  for (const outpost of props.outposts.filter((item) => item.biome === props.map?.biome)) {
    const pixel = outpostPixel(outpost)
    if (!pixel) continue
    const isQuestTarget = questTargetIds.has(outpost.id)
    const marker = new Graphics()
      .roundRect(pixel.x - 8, pixel.y - 8, 16, 16, 4)
      .fill({ color: isQuestTarget ? 0xa78bfa : 0xf6bd4f })
      .stroke({ color: 0xfff4d5, width: 2 })
    markerLayer.addChild(marker)
  }
}

function drawRoute() {
  clearLayer(routeLayer)
  if (!routeLayer || !props.calibration || !props.route) return
  const calibration = props.calibration
  const points = props.route.stops.flatMap((stop) => {
    if (
      stop.biome !== props.map?.biome
      || stop.worldX === null
      || stop.worldY === null
    ) return []
    const pixel = worldToPixel({ x: stop.worldX, y: stop.worldY }, calibration)
    return pixel ? [pixel] : []
  })
  if (points.length < 2) return

  const line = new Graphics().moveTo(points[0].x, points[0].y)
  points.slice(1).forEach((point) => line.lineTo(point.x, point.y))
  line.stroke({ color: 0x9e84ff, width: 4, alpha: 0.82 })
  routeLayer.addChild(line)
}

function resetView() {
  if (!world || !app) return
  const { width, height } = fallbackSize()
  const scale = Math.min(
    1,
    Math.max(0.08, Math.min(app.screen.width / width, app.screen.height / height) * 0.92)
  )
  world.scale.set(scale)
  world.position.set(
    (app.screen.width - width * scale) / 2,
    (app.screen.height - height * scale) / 2
  )
}

function handlePointerDown(event: PointerEvent) {
  dragging = true
  previousPointer = { x: event.clientX, y: event.clientY }
  host.value?.setPointerCapture(event.pointerId)
}

function handlePointerMove(event: PointerEvent) {
  if (!dragging || !world) return
  world.x += event.clientX - previousPointer.x
  world.y += event.clientY - previousPointer.y
  previousPointer = { x: event.clientX, y: event.clientY }
}

function handlePointerUp(event: PointerEvent) {
  dragging = false
  if (host.value?.hasPointerCapture(event.pointerId)) {
    host.value.releasePointerCapture(event.pointerId)
  }
}

function handleWheel(event: WheelEvent) {
  if (!world || !app) return
  event.preventDefault()
  const bounds = app.canvas.getBoundingClientRect()
  const pointer = { x: event.clientX - bounds.left, y: event.clientY - bounds.top }
  const oldScale = world.scale.x
  const nextScale = Math.min(5, Math.max(0.05, oldScale * (event.deltaY < 0 ? 1.12 : 0.89)))
  const local = {
    x: (pointer.x - world.x) / oldScale,
    y: (pointer.y - world.y) / oldScale
  }
  world.scale.set(nextScale)
  world.position.set(pointer.x - local.x * nextScale, pointer.y - local.y * nextScale)
}

onMounted(async () => {
  if (!host.value) return
  app = new Application()
  await app.init({
    antialias: true,
    background: '#0b1119',
    preference: 'webgl',
    resizeTo: host.value
  })
  host.value.appendChild(app.canvas)

  world = new Container()
  terrainLayer = new Container()
  routeLayer = new Container()
  markerLayer = new Container()
  world.addChild(terrainLayer, routeLayer, markerLayer)
  app.stage.addChild(world)

  await drawTerrain()
  drawRoute()
  drawMarkers()

  host.value.addEventListener('pointerdown', handlePointerDown)
  host.value.addEventListener('pointermove', handlePointerMove)
  host.value.addEventListener('pointerup', handlePointerUp)
  host.value.addEventListener('pointercancel', handlePointerUp)
  host.value.addEventListener('wheel', handleWheel, { passive: false })
})

watch(() => props.map, drawTerrain)
watch(
  () => [props.player, props.outposts, props.calibration] as const,
  () => {
    drawMarkers()
    drawRoute()
  },
  { deep: true }
)
watch(() => props.route, drawRoute, { deep: true })

onBeforeUnmount(() => {
  renderToken += 1
  const element = host.value
  element?.removeEventListener('pointerdown', handlePointerDown)
  element?.removeEventListener('pointermove', handlePointerMove)
  element?.removeEventListener('pointerup', handlePointerUp)
  element?.removeEventListener('pointercancel', handlePointerUp)
  element?.removeEventListener('wheel', handleWheel)
  app?.destroy(true, { children: true })
  app = null
  world = null
  terrainLayer = null
  routeLayer = null
  markerLayer = null
})
</script>

<template>
  <section class="map-shell">
    <div class="map-toolbar">
      <div>
        <strong>{{ map?.fileName ?? '地图画布' }}</strong>
        <span>
          {{ map?.pixelStatus === 'ready' ? '真实 PNG 像素' : '仅元数据' }}
          · 拖动平移 · 滚轮缩放
        </span>
      </div>
      <button type="button" @click="resetView">适应画布</button>
    </div>
    <div ref="host" class="map-canvas" aria-label="Crashlands 地图画布"></div>
  </section>
</template>

<style scoped>
.map-shell {
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 18px;
  background: #0b1119;
  box-shadow: var(--shadow);
}

.map-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border-bottom: 1px solid #283142;
}

.map-toolbar div {
  display: grid;
  gap: 3px;
}

.map-toolbar span {
  color: var(--muted);
  font-size: 13px;
}

.map-toolbar button {
  padding: 8px 12px;
  border: 1px solid #374151;
  border-radius: 10px;
  color: var(--text);
  background: #192131;
  cursor: pointer;
}

.map-canvas {
  width: 100%;
  height: min(68vh, 720px);
  min-height: 460px;
  touch-action: none;
  cursor: grab;
}

.map-canvas:active {
  cursor: grabbing;
}
</style>
